import React, { useState } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  TextField,
  Select,
  Button,
  DataTable,
  Modal,
  Badge,
  EmptyState,
  Divider,
} from "@shopify/polaris";

const RULE_TYPE_OPTIONS = [
  { label: "Zaman Bazli", value: "time_based" },
  { label: "Kategori Bazli", value: "category_based" },
  { label: "Kombine", value: "combined" },
];

const BONUS_TYPE_OPTIONS = [
  { label: "Ekstra Puan", value: "extra_points" },
  { label: "Puan Carpani", value: "multiplier" },
];

export default function Challenges() {
  const [challenges, setChallenges] = useState(getMockChallenges());
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(getEmptyForm());

  function getEmptyForm() {
    return {
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      ruleType: "time_based",
      targetCategory: "",
      bonusType: "extra_points",
      bonusValue: "",
      active: true,
    };
  }

  function openCreate() {
    setEditing(null);
    setForm(getEmptyForm());
    setModalOpen(true);
  }

  function openEdit(ch) {
    setEditing(ch);
    setForm({
      title: ch.title,
      description: ch.description,
      startDate: ch.startDate,
      endDate: ch.endDate,
      ruleType: ch.ruleType,
      targetCategory: ch.targetCategory || "",
      bonusType: ch.bonusType,
      bonusValue: String(ch.bonusValue),
      active: ch.active,
    });
    setModalOpen(true);
  }

  function handleSave() {
    if (editing) {
      setChallenges((prev) =>
        prev.map((c) =>
          c.id === editing.id
            ? { ...c, ...form, bonusValue: parseFloat(form.bonusValue) }
            : c
        )
      );
    } else {
      setChallenges((prev) => [
        ...prev,
        {
          id: `ch_${Date.now()}`,
          ...form,
          bonusValue: parseFloat(form.bonusValue),
          participants: 0,
        },
      ]);
    }
    setModalOpen(false);
  }

  function toggleActive(id) {
    setChallenges((prev) =>
      prev.map((c) => (c.id === id ? { ...c, active: !c.active } : c))
    );
  }

  function handleDelete(id) {
    setChallenges((prev) => prev.filter((c) => c.id !== id));
  }

  const now = new Date();

  const rows = challenges.map((ch) => {
    const isActive = ch.active && new Date(ch.startDate) <= now && new Date(ch.endDate) >= now;
    const isPast = new Date(ch.endDate) < now;

    return [
      <BlockStack gap="100">
        <Text fontWeight="bold">{ch.title}</Text>
        <Text variant="bodySm" tone="subdued">{ch.description}</Text>
      </BlockStack>,
      RULE_TYPE_OPTIONS.find((o) => o.value === ch.ruleType)?.label || ch.ruleType,
      ch.bonusType === "multiplier" ? `${ch.bonusValue}x carpan` : `+${ch.bonusValue} puan`,
      `${formatDate(ch.startDate)} - ${formatDate(ch.endDate)}`,
      isActive ? (
        <Badge tone="success">Aktif</Badge>
      ) : isPast ? (
        <Badge>Bitmis</Badge>
      ) : (
        <Badge tone="warning">Pasif</Badge>
      ),
      ch.participants || 0,
      <InlineStack gap="200">
        <Button size="slim" onClick={() => openEdit(ch)}>Duzenle</Button>
        <Button size="slim" onClick={() => toggleActive(ch.id)}>
          {ch.active ? "Durdur" : "Baslat"}
        </Button>
        <Button size="slim" tone="critical" onClick={() => handleDelete(ch.id)}>Sil</Button>
      </InlineStack>,
    ];
  });

  return (
    <Page
      title="Meydan Okumalar"
      subtitle="Sezonluk kampanya ve challenge yonetimi"
      primaryAction={{ content: "Yeni Challenge", onAction: openCreate }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            {challenges.length === 0 ? (
              <EmptyState
                heading="Henuz meydan okuma yok"
                action={{ content: "Challenge Olustur", onAction: openCreate }}
              >
                <p>Musterilerinizi motive etmek icin sezonluk kampanyalar olusturun.</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={["text", "text", "text", "text", "text", "numeric", "text"]}
                headings={["Challenge", "Tip", "Bonus", "Tarih Araligi", "Durum", "Katilim", "Islem"]}
                rows={rows}
                hoverable
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Challenge Duzenle" : "Yeni Challenge Olustur"}
        primaryAction={{ content: "Kaydet", onAction: handleSave }}
        secondaryActions={[{ content: "Iptal", onAction: () => setModalOpen(false) }]}
        large
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Baslik"
              value={form.title}
              onChange={(v) => setForm((p) => ({ ...p, title: v }))}
              autoComplete="off"
              placeholder="ornek: Nisan Ayi Bonus Puan Senligi"
            />
            <TextField
              label="Aciklama"
              value={form.description}
              onChange={(v) => setForm((p) => ({ ...p, description: v }))}
              autoComplete="off"
              multiline={2}
              placeholder="ornek: Bu ay yapacaginiz tum siparislerde 2x puan kazanin!"
            />

            <Divider />

            <InlineStack gap="400">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Baslangic Tarihi"
                  type="date"
                  value={form.startDate}
                  onChange={(v) => setForm((p) => ({ ...p, startDate: v }))}
                  autoComplete="off"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Bitis Tarihi"
                  type="date"
                  value={form.endDate}
                  onChange={(v) => setForm((p) => ({ ...p, endDate: v }))}
                  autoComplete="off"
                />
              </div>
            </InlineStack>

            <Divider />

            <Select
              label="Kural Tipi"
              options={RULE_TYPE_OPTIONS}
              value={form.ruleType}
              onChange={(v) => setForm((p) => ({ ...p, ruleType: v }))}
            />

            {(form.ruleType === "category_based" || form.ruleType === "combined") && (
              <TextField
                label="Hedef Kategori (collection handle)"
                value={form.targetCategory}
                onChange={(v) => setForm((p) => ({ ...p, targetCategory: v }))}
                autoComplete="off"
                placeholder="ornek: rc-arabalar"
                helpText="Shopify collection handle'ini girin"
              />
            )}

            <InlineStack gap="400">
              <div style={{ flex: 1 }}>
                <Select
                  label="Bonus Tipi"
                  options={BONUS_TYPE_OPTIONS}
                  value={form.bonusType}
                  onChange={(v) => setForm((p) => ({ ...p, bonusType: v }))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label={form.bonusType === "multiplier" ? "Carpan Degeri" : "Ekstra Puan"}
                  type="number"
                  value={form.bonusValue}
                  onChange={(v) => setForm((p) => ({ ...p, bonusValue: v }))}
                  autoComplete="off"
                  helpText={form.bonusType === "multiplier" ? "ornek: 2 = 2x puan" : "ornek: 100 = +100 ekstra puan"}
                />
              </div>
            </InlineStack>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
}

function getMockChallenges() {
  return [
    {
      id: "ch1",
      title: "Nisan Bonus Puan Senligi",
      description: "Bu ay tum siparislerde 2x puan kazanin!",
      startDate: "2026-04-01",
      endDate: "2026-04-30",
      ruleType: "time_based",
      targetCategory: "",
      bonusType: "multiplier",
      bonusValue: 2,
      active: true,
      participants: 34,
    },
    {
      id: "ch2",
      title: "RC Araba Haftasi",
      description: "RC Araba kategorisinden alin, +100 bonus puan kazanin",
      startDate: "2026-04-14",
      endDate: "2026-04-21",
      ruleType: "category_based",
      targetCategory: "rc-arabalar",
      bonusType: "extra_points",
      bonusValue: 100,
      active: true,
      participants: 12,
    },
    {
      id: "ch3",
      title: "Mart Aksesuar Kampanyasi",
      description: "Aksesuar kategorisinden hafta sonu alin, %50 ekstra puan",
      startDate: "2026-03-01",
      endDate: "2026-03-31",
      ruleType: "combined",
      targetCategory: "aksesuar-ve-yedek-parca",
      bonusType: "multiplier",
      bonusValue: 1.5,
      active: false,
      participants: 28,
    },
  ];
}
